// src/pages/Login.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import CryptoJS from "crypto-js";
import "../css/login.css";
import { useAuth } from "../security/AuthLogin";
import FullScreenSpinner from "../components/FullScreenSpinner";

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY as string;
if (!ENCRYPTION_KEY) {
  throw new Error(
    "La clave de encriptación (REACT_APP_ENCRYPTION_KEY) no está definida"
  );
}

const API_URL = `${window.location.protocol}//${window.location.hostname}`;

function LoginPage() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { login: authLogin } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!executeRecaptcha) {
        setError("La verificación de reCAPTCHA aún no está lista");
        setLoading(false);
        return;
      }

      // Obtén el token de reCAPTCHA para la acción "login"
      const recaptchaToken = await executeRecaptcha("login");

      // Encripta el usuario y la contraseña
      const encryptedLogin = CryptoJS.AES.encrypt(
        login,
        ENCRYPTION_KEY
      ).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(
        password,
        ENCRYPTION_KEY
      ).toString();

      // Envía al backend login, password y el token de reCAPTCHA
      const response = await axios.post(`${API_URL}/api/login`, {
        login: encryptedLogin,
        password: encryptedPassword,
        recaptchaToken,
      });

      const { name, token } = response.data;
      // Actualiza el estado global de autenticación
      await authLogin(token, name);
      // Redirige al dashboard
      navigate("/dashboard");
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        setError("Credenciales incorrectas. Revisa tu usuario y contraseña.");
      } else if (err.response && err.response.status === 403) {
        setError("Usuario no autorizado.");
      } else {
        setError("Error en el servidor. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <FullScreenSpinner />;
  }

  return (
    <div className="oclem-login">
      <div className="oclem-login__image"></div>
      <div className="oclem-login__form">
        <div className="oclem-login__logo">
          <img src="/images/logo_oclem.png" alt="Logo" />
        </div>
        <form onSubmit={handleSubmit} className="oclem-login__form-container">
          <div className="oclem-login__input-group">
            <label className="oclem-login__label">Nombre de usuario</label>
            <input
              className="oclem-login__input"
              type="text"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              required
              placeholder="Ingresa tu nombre de usuario"
            />
          </div>
          <div className="oclem-login__input-group">
            <label className="oclem-login__label">Contraseña</label>
            <input
              className="oclem-login__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Ingresa tu contraseña"
            />
          </div>
          {error && <p className="oclem-login__error">{error}</p>}
          <button className="oclem-login__button" type="submit">
            Iniciar sesión
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
